<template>
    <div class="page4-container">
        <!-- Box Selection Section -->
        <ul v-if="bestBoxes.length > 0" class="boxes-container">
            <li v-for="box in bestBoxes" :key="box.id">
                <div class="box-container">
                    <div 
                        class="main-image-container" 
                        :class="{ 'selected': isBoxSelected(box) }"
                    >
                        <img 
                            v-if="isBoxSelected(box)" 
                            class="green-check" 
                            src="https://cdn.shopify.com/s/files/1/1363/8433/files/check-circle.svg?v=1658433688"
                            alt="Selected"
                        />
                        <img 
                            @click="selectBox(box)" 
                            class="main-image" 
                            width="280" 
                            height="280" 
                            loading="lazy" 
                            :src="box | getProductImage"
                            :alt="box.title"
                        />
                    </div>
                    <p class="box-title">{{ box.title }}</p>
                    <p class="box-desc">{{ box | moneyFormat }}</p>
                    <button 
                        v-if="isBoxSelected(box)" 
                        @click="changePage(true)" 
                        class="happybox-button green border"
                    >
                        Next
                    </button>
                    <button 
                        v-else 
                        @click="selectBox(box)" 
                        class="happybox-button white border"
                    >
                        Select Design
                    </button>
                </div>
            </li>
        </ul>

        <!-- Extended Boxes Warning Section -->
        <div v-if="extendedProducts && isExtendedBoxes" class="box-warning w-50">
            <img 
                src="https://cdn.shopify.com/s/files/1/1363/8433/files/i_icon_554a1a74-64ad-404e-9f6e-47fe1431b3ea.svg?v=1753797241" 
                class="alert-icon"
                alt="Info"
            />
            <p>
                Want to see more box options? <br/> 
                Remove <strong>{{ extendedProducts }}</strong> to see more designs and sizes!
            </p>
            <div class="flex flex-align-center gap-1vw">
                <div class="flex-1" v-for="box in noExtendedBoxes" :key="box.id">
                    <img 
                        class="w-100 h-auto" 
                        width="280" 
                        height="280" 
                        loading="lazy" 
                        :src="box | getProductImage"
                        :alt="box.title"
                    />
                    <p class="box-desc">{{ box | moneyFormat }}</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
    name: 'Page4',
    
    data() {
        return {
            isSticking: true,
        };
    },

    computed: {
        ...mapGetters([
            'boxProducts',
            'selectedBoxProduct',
            'currentCapacity',
            'selectedAddonProducts',
            'boxQty',
            'maxCapacity',
        ]),

        bestBoxes() {
            // Check if there are fragile products (XLTRIGGER tag)
            const hasFragileProducts = this.selectedAddonProducts.some(product => 
                product.tags.includes("XLTRIGGER")
            );

            // Filter boxes by quantity minimum requirements
            let filteredBoxes = this.filterBoxesByQuantity(this.boxProducts);
            // Flatten box variants for easier processing
            let flattenedBoxes = this.flattenBoxVariants(filteredBoxes);
            // Filter by inventory availability
            let availableBoxes = this.filterBoxesByInventory(flattenedBoxes);
            // Calculate optimal box size based on capacity and fragility
            const optimalBoxSize = this.calculateOptimalBoxSize(availableBoxes, hasFragileProducts);
            // Return boxes matching the optimal size
            return availableBoxes.filter(box => box.variants[0].title == optimalBoxSize);
        },

        currentMaxCapacity() {
            return this.maxCapacity;
        },

        lesserMaxCapacity() {
            // Get the largest non-extended box capacity that's smaller than current max
            const nonExtendedCapacities = this.boxProducts
                .filter(box => !box.title.toLowerCase().includes("extended"))
                .flatMap(box => box.variants)
                .filter(variant => variant.available)
                .map(variant => parseInt(variant.option1))
                .filter(capacity => !isNaN(capacity))
                .sort((a, b) => b - a);

            return nonExtendedCapacities.find(capacity => capacity < this.currentMaxCapacity);
        },

        isExtendedBoxes() {
            return this.currentCapacity > this.lesserMaxCapacity;
        },

        noExtendedBoxes() {
            // Get first 5 non-extended boxes for display
            const nonExtendedBoxes = this.boxProducts
                .filter(box => !box.title.toLowerCase().includes("extended"))
                .flatMap(box => 
                    box.variants.map(variant => ({
                        ...box,
                        variants: [variant]
                    }))
                );

            return nonExtendedBoxes.slice(0, 5);
        },

        extendedProducts() {
            if (!this.isExtendedBoxes) return null;

            // Find products that when removed would bring capacity below extended threshold
            const removableProducts = this.selectedAddonProducts
                .filter(product => !product.tags.some(tag => tag.includes("Overflow_trigger")))
                .sort((a, b) => (b.capacity || 0) - (a.capacity || 0));

            const productsToRemove = [];
            let remainingCapacity = this.currentCapacity;

            for (const product of removableProducts) {
                if (remainingCapacity <= this.lesserMaxCapacity) break;
                
                productsToRemove.push(product.title);
                remainingCapacity -= (product.capacity || 0) * (product.quantity || 1);
            }

            return productsToRemove.join(", ");
        },
    },

    methods: {
        // Box filtering and processing methods
        filterBoxesByQuantity(boxes) {
            return boxes.filter(box => {
                const qtyMinTag = box.tags.find(tag => tag.includes("QTYMIN"));
                if (!qtyMinTag) return true;

                const minQuantity = parseInt(qtyMinTag.split("_").pop());
                return !isNaN(minQuantity) && this.boxQty >= minQuantity;
            });
        },

        flattenBoxVariants(boxes) {
            return boxes.flatMap(box => 
                box.variants.map(variant => ({
                    ...box,
                    variants: [variant]
                }))
            );
        },

        filterBoxesByInventory(boxes) {
            return boxes.filter(box => 
                box.variants[0].inventory_quantity >= this.boxQty
            );
        },

        calculateOptimalBoxSize(boxes, hasFragileProducts) {
            const availableSizes = [...new Set(
                boxes.map(box => box.variants[0].title)
            )].sort();

            if (availableSizes.length === 0) return null;

            if (hasFragileProducts) {
                // For fragile products, avoid the smallest box
                const minSize = Math.min(...availableSizes);
                const suitableSizes = availableSizes.filter(size => 
                    size >= this.currentCapacity && size !== minSize
                );
                return suitableSizes.length > 0 ? Math.min(...suitableSizes) : minSize;
            } else {
                // For non-fragile products, use the smallest box that fits
                const suitableSizes = availableSizes.filter(size => size >= this.currentCapacity);
                return suitableSizes.length > 0 ? Math.min(...suitableSizes) : availableSizes[0];
            }
        },

        // Box selection methods
        selectBox(box) {
            this.$store.commit('updateSelectedMainProduct', box);
            
            if (this.isMobile()) {
                this.scrollToTop();
                this.$emit("changepage", true);
            }
        },

        isBoxSelected(box) {
            return this.selectedBoxProduct && this.selectedBoxProduct.id === box.id;
        },

        changePage() {
            this.$emit("changepage", true);
        },

        // Utility methods
        isMobile() {
            return window.innerWidth < 768;
        },

        scrollToTop() {
            const scrollContainer = document.getElementById('scroll-container');
            if (scrollContainer) {
                scrollContainer.style.scrollBehavior = "unset";
                scrollContainer.scrollTop = 0;
                scrollContainer.style.scrollBehavior = "smooth";
            }
        },
    },

    mounted() {
        // Component mounted logic if needed
    },

    beforeDestroy() {
        // Cleanup logic if needed
    }
};
</script>

<style scoped>

.page4-container{
    height: calc(100% - 60px);
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-bottom: 10%;
}
.main-title{
    color: #0F0C38;
    font-family: 'moret',sans-serif !important;
    font-size: 2.5vw;
    margin-top: 2%;
    letter-spacing: 0.03em;
}

.main-subtitle img{
    width: 1.5%;
    margin: 0% 0.2%;
}

.main-subtitle{
    font-size: 1.2vw;
    color: #3f3d60;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
}

.boxes-container{
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    list-style: none;
    justify-content: center;
    margin: unset;
    padding: 3% 10% 5%;
    align-items: center;
    gap: 20px 2.66%;
}

.boxes-container li{
    width: 23%;
}

.box-container{
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.main-image-container{
    width: 100%;
    border-radius: 4px;
    position: relative;
    background: url(https://cdn.shopify.com/s/files/1/1363/8433/files/cream_shape.png?v=1721312299) no-repeat;
    background-size: contain;
}

.main-image-container.selected{
    border: 1px solid #16A34A;
}

.green-check{
    position: absolute;
    top: 0;
    left: 0;
}
.main-image{
    width: 100%;
    box-sizing: border-box;
    cursor: pointer;
    position: relative;
    height: auto;
}

.main-image:hover{
    animation: MoveUpDown 2.5s ease-in-out infinite;
}

.box-title{
    font-size: 1.1vw;
    font-weight: bold;
    padding-top: 5%;
}

.box-desc{
    color: #A186C4 !important;
    font-size: 1vw;
    line-height: 1.4;
    padding-bottom: 5%;
}

.iframe-container{
    display: flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    background-color: rgba(0, 0, 0, 0.2);
    z-index: 1;
}

.close-iframe{
    font-size: 2vw;
    cursor: pointer;
    position: absolute;
    top: 10%;
    right: 20%;
}

.content-header{
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    padding-top: 1%;
}

.content-header .title{
    font-size: 3.5vw;
    line-height: 1;
    font-family: Founders Grotesk, sans-serif;
    margin-bottom: 0.5rem;
}

.content-header .subtitle{
    font-size: 1.1vw;
    line-height: 1.2;
    text-align: center;
}

.box-warning{
    border: 1px solid #D0CFBB;
    background-color: #fdfde2;
    font-size: 1vw;
    padding: 0.5rem 1rem;
    color: #0f0c38;
    text-align: center;
    max-width: 80%;
    border-radius: 4px;
    line-height: 1.2;
}

.box-warning .alert-icon{
    width: 20px;
}

@media screen and (max-width: 767px){
    .main-title{
        font-size: 5vw;
    }
    .main-subtitle{
        font-size: 3vw;
        text-align: center;
    }

    .main-subtitle img {
        width: 4%;
        margin: 0% 1%;
    }

    .boxes-container{
        gap: 20px 4%;
        padding-bottom: 10%;
    }

    .boxes-container li{
        width: 48%;
    }

    .box-desc{
        font-size: 3vw;
    }

    .box-title{
        font-size: 3.5vw;
    }

    .iframe-container{
        display: flex;
        align-items: center;
        justify-content: center;
        position: fixed;
        top: 0;
        left: 0;
        height: 100%;
        width: 100%;
        background-color: rgba(0, 0, 0, 0.2);
        z-index: 1;
    }

    .close-iframe{
        font-size: 8vw;
        cursor: pointer;
        position: absolute;
        top: 0%;
        right: 5%;
    }

    .box-container .happybox-button{
        width: 100%;
        padding: 2%;
    }

    .content-header{
        padding-top: 5%;
    }

    .content-header .title{
        font-size: 6vw;
        margin-bottom: 0.5rem;
    }

    .content-header .subtitle{
        font-size: 3.5vw;
    }

    .box-warning{
        font-size: 3.5vw;
        max-width: 90%;
        margin-bottom: 40%;
        width: auto;
    }
}
</style>